import { ReactNode, useMemo } from "react"
import { useIntl } from "react-intl"

import classNames from "classnames"
import { capitalize } from "lodash"

import { Accordion, Button, CopyToClipboard, ICONS, StaffBodyTextS, StaffBodyTextXS } from "@doktor-se/bones-ui"
import { SymptomBubble } from "@doktor-se/bones-ui/dist/web-shared/components"
import {
  Account,
  AppPlatform,
  Category,
  Conversation,
  ConversationState,
  Profile,
  SeenConversation,
  Staff
} from "@doktor-se/bones-ui/dist/web-shared/types"

import { appStatic } from "config"
import { useAppSelector } from "lib/hooks"
import { useRequestDecision } from "lib/hooks/useRequestDecision"
import { categorySelectors } from "reducers/categories"
import { ReassignRequestConversation, reassignRequestsSelectors } from "reducers/conversations/conversations.reducer"

import BookingHistory from "components/booking/booking/BookingHistory"

import { formatPnr, getAgeFromPnr, getGenderFromPnr } from "./utils"

import styles from "./ConversationDetails.module.scss"

export const ConversationDetails = ({
  conversation,
  patient,
  profile,
  features,
  showReopenDialog,
  showReportDialog,
  showReassignDialog
}: {
  conversation: Conversation
  patient: Account
  profile?: Profile
  features: {
    hasAssignToOtherStaff: boolean
    hasReopen: boolean
  }
  showReopenDialog: () => void
  showReportDialog: () => void
  showReassignDialog: () => void
}) => {
  const intl = useIntl()
  const user = useAppSelector(state => state.auth.user)
  const { handleRequestDecision } = useRequestDecision()
  const category = useAppSelector(state =>
    conversation.categoryId ? categorySelectors.selectById(state, conversation.categoryId) : undefined
  )
  const allStaff = useAppSelector(state => state.users.staff)
  const reassignRequests = useAppSelector(reassignRequestsSelectors.selectAll)
  const isChildConv = !!profile

  // Check if this conversation has a pending request for the current user
  const pendingRequest = useMemo(() => {
    return reassignRequests.find(req => req.id === conversation.id && req.reassignTo === user?.id)
  }, [reassignRequests, conversation.id, user?.id])

  const filterStaff = (seenConversationList: SeenConversation[]) => {
    const userIds = new Set(seenConversationList.map(seenConv => seenConv.userId))
    return allStaff.filter(staff => userIds.has(staff.id))
  }

  const assignedStaff = useMemo(
    () => allStaff.find(staff => staff.id === conversation.assignedStaffId),
    [allStaff, conversation.assignedStaffId]
  )

  const handleAcceptRequest = () => {
    if (pendingRequest) {
      handleRequestDecision(pendingRequest, true, true)
    }
  }
  return (
    <div className={styles.conversationDetails}>
      {patient.data && (
        <>
          <PatientDetails
            name={isChildConv ? profile!.name : patient.displayName}
            parentName={isChildConv ? patient.displayName : undefined}
            pnr={isChildConv ? profile!.data.securityNumber : patient.pnr}
          />
          <CommentDetail comment={patient.comment ?? ""} />
          <ErrandDetails
            category={category}
            state={conversation.state}
            seenBy={filterStaff(conversation.seen ?? [])}
            assignedStaff={assignedStaff}
          />
          <ListedDetail listed={conversation.customerMetadata?.listedAtClinicName} />
          <DeviceDetails device={conversation?.platforms?.at(0)} conversationId={conversation.id} />
          {conversation.metadata?.bookingId && (
            <div className={styles.accordionWrapper}>
              <Accordion
                title={intl.formatMessage({ id: "label.errand.bookingHistory" })}
                content={<BookingHistory bookingId={conversation.metadata.bookingId} />}
              />
            </div>
          )}
          <Buttons
            conversation={conversation}
            features={features}
            showReopenDialog={showReopenDialog}
            showReportDialog={showReportDialog}
            showReassignDialog={showReassignDialog}
            pendingRequest={pendingRequest}
            onAcceptRequest={handleAcceptRequest}
            onDeclineRequest={() => pendingRequest && handleRequestDecision(pendingRequest, false, false)}
          />
        </>
      )}
    </div>
  )
}

const DetailsBlock = ({ children }: { children: ReactNode }) => {
  return <div className={styles.detailsBlock}>{children}</div>
}

interface DetailDataValue {
  text: string
  icon?: ReactNode
}

const DetailData = ({
  title,
  values,
  copyLabel,
  emphasized
}: {
  title: string
  values: DetailDataValue[]
  copyLabel?: string
  emphasized?: boolean
}) => (
  <div className={styles.detailData}>
    <StaffBodyTextXS>{title}</StaffBodyTextXS>

    {values.map(value => (
      <div key={value.text} className={styles.detailDataValue}>
        <div>
          {value.icon && value.icon}
          {emphasized ? <StaffBodyTextS>{value.text}</StaffBodyTextS> : <StaffBodyTextXS>{value.text}</StaffBodyTextXS>}
        </div>
        {copyLabel && (
          <CopyToClipboard
            copyText={values.map(value => value.text).join(", ")}
            confirmationText="Copied"
            buttonType="button"
            aria-label={copyLabel}
            buttonSmall
            buttonColor="primaryLight"
            buttonVariant="primary"
          />
        )}
      </div>
    ))}
  </div>
)

const PatientDetails = ({ name, parentName, pnr }: { name: string; parentName?: string; pnr?: string }) => {
  const intl = useIntl()
  const pnrFormatted = formatPnr(pnr)
  const age = getAgeFromPnr(pnr)
  const gender = getGenderFromPnr(pnr)

  return (
    <DetailsBlock>
      <DetailData
        title={intl.formatMessage({ id: "label.overview.name" })}
        values={[{ text: name, icon: parentName ? <ICONS.Child width={25} height={25} /> : null }]}
        emphasized
      />
      <DetailData
        title={intl.formatMessage({ id: "label.overview.pnr" })}
        values={[{ text: pnrFormatted }]}
        copyLabel={intl.formatMessage({ id: "label.copyNumber" })}
      />
      <div className={styles.ageGender}>
        <DetailData
          title={intl.formatMessage({ id: "label.errand.age" })}
          values={[
            {
              text: age
                ? `${age} ${intl.formatMessage({ id: "label.errand.years" })}`
                : intl.formatMessage({ id: "label.unknown" })
            }
          ]}
        />
        <DetailData
          title={intl.formatMessage({ id: "label.errand.gender" })}
          values={[
            {
              text: intl.formatMessage({
                id: gender === "unknown" ? "label.unknown" : `label.errand.${gender}`
              })
            }
          ]}
        />
      </div>
      {parentName ? (
        <>
          <hr />
          <DetailData title={intl.formatMessage({ id: "label.errand.parentsName" })} values={[{ text: parentName }]} />
        </>
      ) : null}
    </DetailsBlock>
  )
}

const CommentDetail = ({ comment }: { comment: string }) => {
  const intl = useIntl()

  return (
    <DetailsBlock>
      <DetailData title={intl.formatMessage({ id: "label.errand.comment" })} values={[{ text: comment }]} />
    </DetailsBlock>
  )
}

const ErrandDetails = ({
  category,
  state,
  seenBy,
  assignedStaff
}: {
  category?: Category
  state: ConversationState
  seenBy: Staff[]
  assignedStaff?: Staff
}) => {
  const intl = useIntl()
  return (
    <DetailsBlock>
      {category ? (
        <>
          <DetailData
            title={intl.formatMessage({ id: "label.errand.errandType" })}
            values={[{ text: category?.name ?? "", icon: <SymptomBubble appStatic={appStatic} symptom={category} /> }]}
          />
          <DetailData
            title={intl.formatMessage({ id: "label.errand.statusOfErrand" })}
            values={[
              {
                text:
                  state === "closed"
                    ? capitalize(intl.formatMessage({ id: "label.closed" }))
                    : (state === "opened" || state === "booked") && assignedStaff
                      ? intl.formatMessage({ id: "label.assigned_to" }) + " " + assignedStaff.displayName
                      : intl.formatMessage({ id: "label.in_queue" })
              }
            ]}
          />
          <DetailData
            title={intl.formatMessage({ id: "label.errand.errandHasBeenSeenBy" })}
            values={seenBy.map(s => ({ text: `${s.displayName} (${s.role})` }))}
          />
        </>
      ) : null}
    </DetailsBlock>
  )
}

const ListedDetail = ({ listed }: { listed?: string }) => {
  const intl = useIntl()

  return (
    <DetailsBlock>
      <div>{intl.formatMessage({ id: "label.overview.listed" })}</div>
      <div className={classNames(styles.listingInfo, { [styles.listed]: listed, [styles.unlisted]: !listed })}>
        {listed ?? intl.formatMessage({ id: "label.unlisted" })}
      </div>
    </DetailsBlock>
  )
}
const DeviceDetails = ({ device, conversationId }: { device?: AppPlatform; conversationId: string }) => {
  const intl = useIntl()

  const deviceValues = []
  if (device?.os) {
    deviceValues.push({ text: device.os })
  }
  if (device?.model) {
    deviceValues.push({ text: device.model })
  }
  if (device?.appVersion) {
    deviceValues.push({ text: device.appVersion })
  }

  return (
    <DetailsBlock>
      <DetailData
        title={intl.formatMessage({ id: "label.device" })}
        values={deviceValues.length > 0 ? deviceValues : [{ text: "" }]}
      />
      <DetailData
        title={intl.formatMessage({ id: "label.conversationId" })}
        values={[{ text: conversationId }]}
        copyLabel={intl.formatMessage({ id: "label.copyId" })}
      />
    </DetailsBlock>
  )
}

const Buttons = ({
  conversation,
  features,
  showReopenDialog,
  showReportDialog,
  showReassignDialog,
  pendingRequest,
  onAcceptRequest,
  onDeclineRequest
}: {
  conversation: Conversation
  features: { hasReopen: boolean; hasAssignToOtherStaff: boolean }
  showReopenDialog: () => void
  showReportDialog: () => void
  showReassignDialog: () => void
  pendingRequest?: ReassignRequestConversation
  onAcceptRequest: () => void
  onDeclineRequest: () => void
}) => {
  const intl = useIntl()

  return (
    <div className={`${styles.buttons} ${pendingRequest ? styles.pendingVisible : ""}`}>
      {pendingRequest && (
        <>
          <Button onPress={onAcceptRequest} color="primary" variant="primary" small>
            {intl.formatMessage({ id: "requests.take_over.accept" })}
          </Button>
          <Button onPress={onDeclineRequest} color="primary" variant="outline" small>
            {intl.formatMessage({ id: "requests.take_over.decline" })}
          </Button>
        </>
      )}
      {!pendingRequest && conversation.state === "closed" && features.hasReopen && (
        <Button onPress={showReopenDialog} Icon={<ICONS.Replay />} color="primary" variant="primary" small>
          {intl.formatMessage({ id: "label.errand.reopen" })}
        </Button>
      )}
      {!pendingRequest && conversation.state !== "closed" && features.hasAssignToOtherStaff && (
        <Button onPress={showReassignDialog} Icon={<ICONS.OpenInBrowser />} color="primary" variant="primary" small>
          {intl.formatMessage({ id: "label.errand.assign" })}
        </Button>
      )}
      <Button onPress={showReportDialog} Icon={<ICONS.Flag />} color="primary" variant="outline" small>
        {intl.formatMessage({ id: "label.errand.reportIssue" })}
      </Button>
    </div>
  )
}

export default ConversationDetails
